/** @file Direction modes in display order with their catalog labels, and whether a direction may mirror now. */
import { COUNTERPART } from '@pose2d/commands/direction/counterpart';
import { names } from '@pose2d/format/names';
import type { Dir, DirectionEntry, DirectionsDoc } from '@pose2d/format/types';
import type { CatalogKey } from '@pose2d/i18n/types';

type Mode = DirectionEntry['mode'];

const LABEL: Readonly<Record<Mode, CatalogKey>> = { authored: 'panels.directions.authored', mirror: 'panels.directions.mirror', mirrorFix: 'panels.directions.mirrorFix' };

/** True when `dir` may mirror (§5.8 as direction.setMode checks it): its counterpart is authored and nothing mirrors `dir`. */
function canMirror(set: DirectionsDoc, dir: Dir): boolean {
  const source = COUNTERPART[dir];
  if (source === undefined || set.entries[source]?.mode !== 'authored') return false;
  return !names.DIRS.some((d) => {
    const e = set.entries[d];
    return e !== undefined && e.mode !== 'authored' && e.source === dir;
  });
}

/** Mode labels and the mirror rule for the Directions panel (the command stays the authority). */
export const directionModes = {
  /** The modes in the order the Select lists them. */
  MODES: ['authored', 'mirror', 'mirrorFix'] as const satisfies readonly Mode[],
  /** Catalog key of a mode's label. */
  label: (mode: Mode): CatalogKey => LABEL[mode],
  /** See canMirror. */
  canMirror,
};
